import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Loader2, Sparkles, FlaskConical, Upload, RefreshCw } from "lucide-react";

const FIELDS = [
  { key: "common_name", label: "Common Name" },
  { key: "chemical_class", label: "Chemical Class" },
  { key: "cas_number", label: "CAS Number", mono: true },
  { key: "pubchem_id", label: "PubChem CID", mono: true },
  { key: "molecular_formula", label: "Molecular Formula", mono: true },
  { key: "molecular_weight", label: "Molecular Weight", mono: true },
  { key: "inchikey", label: "InChIKey", mono: true },
  { key: "iupac_name", label: "IUPAC Name" },
];

export default function Substances() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [detail, setDetail] = useState(null);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [debouncedQ, setDebouncedQ] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/substances/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import complete — ${data.imported} added, ${data.updated} updated (${data.total} total)`);
      qc.invalidateQueries({ queryKey: ["substances"] });
      qc.invalidateQueries({ queryKey: ["substance-facets"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const { data: facets } = useQuery({
    queryKey: ["substance-facets"],
    queryFn: async () => (await api.get("/substance-facets")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["substances", debouncedQ, cls],
    queryFn: async () => (await api.get(`/substances?q=${encodeURIComponent(debouncedQ)}&chemical_class=${encodeURIComponent(cls)}`)).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => editId ? api.put(`/substances/${editId}`, payload) : api.post("/substances", payload),
    onSuccess: () => { toast.success(editId ? "Substance updated" : "Substance added"); qc.invalidateQueries({ queryKey: ["substances"] }); setOpen(false); setForm({}); setEditId(null); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/substances/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["substances"] }); setDetail(null); },
  });

  const syncMut = useMutation({
    mutationFn: async (id) => (await api.post(`/substances/${id}/sync-pubchem`)).data,
    onSuccess: (data) => {
      if (data.pubchem_sync_status === "not_found") toast.warning("Not found on PubChem for this compound.");
      else toast.success("Synced with PubChem");
      setDetail(data);
      qc.invalidateQueries({ queryKey: ["substances"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openNew = () => { setForm({}); setEditId(null); setOpen(true); };
  const openEdit = (row) => { setForm({ ...row }); setEditId(row.id); setOpen(true); setDetail(null); };

  const fetchStructure = async () => {
    if (!form.compound_name?.trim()) return toast.error("Type a chemical name first");
    setFetching(true);
    try {
      const { data } = await api.get(`/pubchem?name=${encodeURIComponent(form.compound_name)}`);
      setForm((s) => ({ ...s, ...data }));
      toast.success(`Structure loaded for ${data.compound_name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFetching(false); }
  };

  const submit = () => { if (!form.compound_name?.trim()) return toast.error("Compound name is required"); saveMut.mutate(form); };
  const items = data || [];
  const pageSize = 24;
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = items.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Substances</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{items.length} substance{items.length === 1 ? "" : "s"}{cls ? ` in ${cls.replace(/_/g, " ")}` : " in the database"}.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" hidden onChange={doImport} data-testid="import-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-excel-btn">
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {importing ? "Importing…" : "Import Excel"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="add-substance-btn"><Plus className="h-4 w-4 mr-2" />Add Substance</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "New"} Substance</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label className="text-xs">Chemical Name</Label>
                <div className="flex gap-2">
                  <Input value={form.compound_name || ""} onChange={(e) => setForm({ ...form, compound_name: e.target.value })} placeholder="e.g. LSD, Psilocybin, Ketamine" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), fetchStructure())} data-testid="substance-name" />
                  <Button type="button" variant="secondary" onClick={fetchStructure} disabled={fetching} data-testid="fetch-structure-btn">
                    {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" />Fetch Structure</>}
                  </Button>
                </div>
              </div>

              {form.structure_image && (
                <div className="flex justify-center border border-border rounded-lg p-3 bg-white">
                  <img src={form.structure_image} alt="structure" className="h-40 object-contain" data-testid="structure-preview" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-xs">{f.label}</Label>
                    <Input className={f.mono ? "font-mono text-xs" : ""} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`substance-${f.key}`} />
                  </div>
                ))}
              </div>
              <div className="space-y-1.5"><Label className="text-xs">SMILES</Label><Input className="font-mono text-xs" value={form.smiles || ""} onChange={(e) => setForm({ ...form, smiles: e.target.value })} data-testid="substance-smiles" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Effects</Label><Textarea value={form.effects || ""} onChange={(e) => setForm({ ...form, effects: e.target.value })} data-testid="substance-effects" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Mechanism of Action</Label><Textarea value={form.mechanism_of_action || ""} onChange={(e) => setForm({ ...form, mechanism_of_action: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Notes / References</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saveMut.isPending} data-testid="substance-save">{saveMut.isPending ? "Saving…" : editId ? "Update" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search substances…" className="pl-9" data-testid="substance-search" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={() => { setCls(""); setPage(1); }}
          data-testid="filter-all"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${cls === "" ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}
        >
          All
        </button>
        {(facets || []).map((f) => (
          <button
            key={f.value}
            onClick={() => { setCls(f.value); setPage(1); }}
            data-testid={`filter-${f.value}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${cls === f.value ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}
          >
            {f.value.replace(/_/g, " ")} <span className="opacity-60">{f.count}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-16 text-center text-sm text-muted-foreground" data-testid="substances-empty">
          {q || cls ? "No substances match your search or filter." : "No substances yet. Click “Import Excel” to upload your file, or “Add Substance”."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {paged.map((s) => (
            <div key={s.id} className="border border-border rounded-lg bg-card overflow-hidden group" data-testid={`substance-card-${s.id}`}>
              <button onClick={() => setDetail(s)} className="relative w-full h-36 bg-white flex items-center justify-center border-b border-border">
                <FlaskConical className="h-10 w-10 text-muted-foreground" />
                {s.structure_image && (
                  <img
                    src={s.structure_image}
                    alt={s.compound_name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="absolute inset-0 w-full h-full object-contain p-2 bg-white"
                  />
                )}
              </button>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.compound_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.molecular_formula || "—"} {s.molecular_weight ? `· ${s.molecular_weight} g/mol` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`substance-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`substance-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete substance?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delMut.mutate(s.id)} data-testid="substance-confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                {s.chemical_class && <Badge variant="secondary" className="mt-2 text-[10px]">{s.chemical_class}</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && items.length > pageSize && (
        <div className="flex items-center justify-between mt-6 text-sm">
          <span className="text-muted-foreground">Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, items.length)} of {items.length}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} data-testid="subs-prev">Previous</Button>
            <span className="font-mono text-xs text-muted-foreground">{currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)} data-testid="subs-next">Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{detail?.compound_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              {detail.structure_image && <div className="flex justify-center border border-border rounded-lg p-3 bg-white"><img src={detail.structure_image} alt={detail.compound_name} className="h-44 object-contain" /></div>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {FIELDS.map((f) => detail[f.key] ? <div key={f.key}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{f.label}</div><div className={f.mono ? "font-mono text-xs break-all" : "break-words"}>{detail[f.key]}</div></div> : null)}
              </div>
              {detail.smiles && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">SMILES</div><div className="font-mono text-xs break-all">{detail.smiles}</div></div>}
              {detail.effects && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Effects</div><div className="text-sm">{detail.effects}</div></div>}
              {detail.mechanism_of_action && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Mechanism of Action</div><div className="text-sm">{detail.mechanism_of_action}</div></div>}
              {detail.notes && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</div><div className="text-sm whitespace-pre-wrap">{detail.notes}</div></div>}
              {(() => {
                const hide = new Set(["id", "_id", "created_at", "updated_at", "created_by", "structure_image", "compound_name", "smiles", "effects", "mechanism_of_action", "molecular_mechanism", "notes", ...FIELDS.map((f) => f.key)]);
                const extra = Object.entries(detail).filter(([k, v]) => !hide.has(k) && v != null && typeof v !== "object" && String(v).trim() !== "");
                if (extra.length === 0) return null;
                return (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">All Fields</div>
                    <div className="grid grid-cols-1 gap-2">
                      {extra.map(([k, v]) => (
                        <div key={k} className="border-b border-border/60 pb-1.5">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                          <div className="text-sm break-words whitespace-pre-wrap">{String(v)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {detail.pubchem && (
                <div className="border border-brand/30 rounded-lg p-3 bg-brand/5">
                  <div className="text-[11px] uppercase tracking-wider text-brand mb-2">PubChem (live){detail.pubchem.fetched_at ? ` · ${detail.pubchem.fetched_at.slice(0, 10)}` : ""}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[["CID", "cid"], ["Formula", "molecular_formula"], ["MW", "molecular_weight"], ["XLogP", "xlogp"], ["TPSA", "tpsa"], ["H-Donors", "hbond_donor"], ["H-Acceptors", "hbond_acceptor"], ["InChIKey", "inchikey"]].map(([l, k]) => detail.pubchem[k] != null && (
                      <div key={k}><span className="text-muted-foreground">{l}: </span><span className="font-mono break-all">{String(detail.pubchem[k])}</span></div>
                    ))}
                  </div>
                </div>
              )}
              <Button variant="secondary" className="w-full" disabled={syncMut.isPending} onClick={() => syncMut.mutate(detail.id)} data-testid="sync-pubchem-btn">
                {syncMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sync with PubChem
              </Button>
              <Button variant="outline" className="w-full" onClick={() => openEdit(detail)}><Pencil className="h-4 w-4 mr-2" />Edit</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
